import { HttpErrorResponse, type HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type {
  AppError,
  AppErrorPresentation,
  AppValidationErrors,
} from '@core/error-handling/app-error.types';
import { TimeoutError } from 'rxjs';

interface HttpErrorMappingContext {
  readonly method: string;
  readonly timeoutMs?: number | undefined;
  readonly url: string;
}

@Injectable({ providedIn: 'root' })
export class AppErrorMapperService {
  public fromHttpError(
    request: HttpRequest<unknown>,
    error: unknown,
    context: {
      readonly timeoutMs?: number;
    },
  ): AppError {
    if (error instanceof TimeoutError) {
      return this.createError({
        cause: error,
        kind: 'timeout',
        message: 'Сервер не успел ответить за отведенное время.',
        method: request.method,
        presentation: 'dialog',
        severity: 'error',
        title: 'Превышено время ожидания',
        url: request.urlWithParams,
        isRetryable: true,
      });
    }

    if (error instanceof HttpErrorResponse) {
      return this.fromHttpResponse(error, {
        method: request.method,
        timeoutMs: context.timeoutMs,
        url: request.urlWithParams,
      });
    }

    return this.createError({
      cause: error,
      kind: 'unexpected',
      message: 'Произошла непредвиденная ошибка при выполнении запроса.',
      method: request.method,
      presentation: 'dialog',
      severity: 'error',
      title: 'Непредвиденная ошибка',
      url: request.urlWithParams,
      isRetryable: false,
    });
  }

  public fromRuntimeError(error: unknown): AppError {
    if (isErrorLike(error)) {
      return this.createError({
        cause: error,
        kind: 'unexpected',
        message: error.message || 'Произошла непредвиденная ошибка приложения.',
        presentation: 'dialog',
        severity: 'error',
        title: 'Ошибка приложения',
        isRetryable: false,
      });
    }

    return this.createError({
      cause: error,
      kind: 'unexpected',
      message: 'Произошла непредвиденная ошибка приложения.',
      presentation: 'dialog',
      severity: 'error',
      title: 'Ошибка приложения',
      isRetryable: false,
    });
  }

  private fromHttpResponse(error: HttpErrorResponse, context: HttpErrorMappingContext): AppError {
    const validationErrors = this.extractValidationErrors(error.error);
    const backendCode = this.extractBackendCode(error.error);
    const backendMessage = this.extractBackendMessage(error.error) ?? error.message;

    if (error.status === 0) {
      return this.createError({
        backendCode,
        backendMessage,
        cause: error,
        kind: 'network',
        message: 'Не удалось связаться с сервером. Проверьте подключение к сети.',
        method: context.method,
        presentation: 'dialog',
        severity: 'error',
        title: 'Ошибка сети',
        url: context.url,
        isRetryable: true,
      });
    }

    if (validationErrors) {
      return this.createError({
        backendCode,
        backendMessage,
        cause: error,
        kind: 'validation',
        message: 'Некоторые введенные данные заполнены некорректно.',
        method: context.method,
        presentation: 'inline',
        severity: 'warn',
        statusCode: error.status,
        title: 'Ошибка валидации',
        url: context.url,
        validationErrors,
        isRetryable: false,
      });
    }

    switch (error.status) {
      case 401:
        return this.createError({
          backendCode,
          backendMessage,
          cause: error,
          kind: 'unauthorized',
          message: 'Сессия отсутствует или истекла. Выполните вход снова.',
          method: context.method,
          presentation: 'dialog',
          severity: 'warn',
          statusCode: error.status,
          title: 'Требуется авторизация',
          url: context.url,
          isRetryable: false,
        });
      case 403:
        return this.createError({
          backendCode,
          backendMessage,
          cause: error,
          kind: 'forbidden',
          message: 'У вас нет прав для выполнения этого действия.',
          method: context.method,
          presentation: 'dialog',
          severity: 'warn',
          statusCode: error.status,
          title: 'Доступ запрещен',
          url: context.url,
          isRetryable: false,
        });
      case 404:
        return this.createError({
          backendCode,
          backendMessage,
          cause: error,
          kind: 'notFound',
          message: 'Запрошенный ресурс не найден.',
          method: context.method,
          presentation: 'dialog',
          severity: 'warn',
          statusCode: error.status,
          title: 'Не найдено',
          url: context.url,
          isRetryable: false,
        });
      case 409:
        return this.createError({
          backendCode,
          backendMessage,
          cause: error,
          kind: 'conflict',
          message: 'Операцию невозможно выполнить из-за конфликта с текущим состоянием данных.',
          method: context.method,
          presentation: 'dialog',
          severity: 'warn',
          statusCode: error.status,
          title: 'Конфликт данных',
          url: context.url,
          isRetryable: false,
        });
      default:
        if (error.status >= 500) {
          return this.createError({
            backendCode,
            backendMessage,
            cause: error,
            kind: 'server',
            message: 'На сервере произошла ошибка при обработке запроса.',
            method: context.method,
            presentation: 'dialog',
            severity: 'error',
            statusCode: error.status,
            title: 'Ошибка сервера',
            url: context.url,
            isRetryable: true,
          });
        }

        return this.createError({
          backendCode,
          backendMessage,
          cause: error,
          kind: 'backend',
          message: backendMessage || 'Не удалось выполнить запрос.',
          method: context.method,
          presentation: 'dialog',
          severity: 'error',
          statusCode: error.status,
          title: 'Ошибка запроса',
          url: context.url,
          isRetryable: false,
        });
    }
  }

  private createError(input: {
    readonly kind: AppError['kind'];
    readonly title: string;
    readonly message: string;
    readonly severity: AppError['severity'];
    readonly presentation: AppErrorPresentation;
    readonly isRetryable: boolean;
    readonly cause?: unknown;
    readonly method?: string;
    readonly url?: string;
    readonly statusCode?: number | undefined;
    readonly backendCode?: number | string | undefined;
    readonly backendMessage?: string | undefined;
    readonly validationErrors?: AppValidationErrors | undefined;
  }): AppError {
    return {
      id: this.createId(),
      source: input.method || input.url || input.statusCode !== undefined ? 'http' : 'runtime',
      kind: input.kind,
      severity: input.severity,
      presentation: input.presentation,
      title: input.title,
      message: input.message,
      timestamp: new Date().toISOString(),
      isRetryable: input.isRetryable,
      ...(input.statusCode !== undefined ? { statusCode: input.statusCode } : {}),
      ...(input.backendCode !== undefined ? { backendCode: input.backendCode } : {}),
      ...(input.backendMessage !== undefined ? { backendMessage: input.backendMessage } : {}),
      ...(input.method !== undefined ? { method: input.method } : {}),
      ...(input.url !== undefined ? { url: input.url } : {}),
      ...(input.validationErrors !== undefined ? { validationErrors: input.validationErrors } : {}),
      ...(input.cause !== undefined ? { cause: input.cause } : {}),
    };
  }

  private extractBackendCode(value: unknown): number | string | undefined {
    if (!isRecord(value)) {
      return undefined;
    }

    const backendStatus = value['status'];
    const code = value['code'];

    if (typeof code === 'number' || typeof code === 'string') {
      return code;
    }

    if (typeof backendStatus === 'number' || typeof backendStatus === 'string') {
      return backendStatus;
    }

    return undefined;
  }

  private extractBackendMessage(value: unknown): string | undefined {
    if (!isRecord(value)) {
      return undefined;
    }

    const candidates = [value['message'], value['error'], value['title'], value['description']];

    return candidates.find((candidate): candidate is string => typeof candidate === 'string');
  }

  private extractValidationErrors(value: unknown): AppValidationErrors | undefined {
    if (!isRecord(value)) {
      return undefined;
    }

    const errors = value['errors'];
    const status = value['status'];

    if (!isRecord(errors)) {
      return status === -4 ? {} : undefined;
    }

    const entries = Object.entries(errors).filter((entry): entry is [string, string] => {
      return typeof entry[1] === 'string';
    });

    if (entries.length === 0) {
      return status === -4 ? {} : undefined;
    }

    return Object.fromEntries(entries);
  }

  private createId(): string {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return globalThis.crypto.randomUUID();
    }

    return `app-error-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

function isErrorLike(value: unknown): value is Error {
  return value instanceof Error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
